import React, { useState, useEffect, useRef } from 'react';
import { Palette, Type, Upload, Eye, Save, Loader2, Check, RotateCcw, Sparkles } from 'lucide-react';
import { Button } from '../../components/ui/button';
import axios from 'axios';
import { useAuth, API } from '../../App';
import { toast } from 'sonner';

// Curated colour palettes
const COLOUR_PALETTES = [
  { name: 'Ocean', primary: '#0F4C81', accent: '#4ECDC4' },
  { name: 'Forest', primary: '#2D5016', accent: '#8BC34A' },
  { name: 'Sunset', primary: '#D84315', accent: '#FF9800' },
  { name: 'Midnight', primary: '#1A1A2E', accent: '#E94560' },
  { name: 'Royal', primary: '#4A148C', accent: '#CE93D8' },
  { name: 'Earth', primary: '#5D4037', accent: '#BCAAA4' },
  { name: 'Coral', primary: '#BF360C', accent: '#FF8A65' },
  { name: 'Nordic', primary: '#263238', accent: '#80CBC4' },
  { name: 'Ember', primary: '#B71C1C', accent: '#FFD54F' },
  { name: 'Sage', primary: '#33691E', accent: '#C5E1A5' },
  { name: 'Slate', primary: '#37474F', accent: '#90A4AE' },
  { name: 'Gold', primary: '#1a1a1a', accent: '#F6ADBC' },
];

// Curated font set
const FONT_OPTIONS = [
  { name: 'Inter', value: 'Inter, sans-serif', style: 'Clean and modern' },
  { name: 'DM Sans', value: '"DM Sans", sans-serif', style: 'Geometric and warm' },
  { name: 'Poppins', value: 'Poppins, sans-serif', style: 'Friendly and rounded' },
  { name: 'Playfair Display', value: '"Playfair Display", serif', style: 'Elegant and editorial' },
  { name: 'Space Grotesk', value: '"Space Grotesk", sans-serif', style: 'Tech-forward' },
  { name: 'Merriweather', value: 'Merriweather, serif', style: 'Traditional and trustworthy' },
  { name: 'Outfit', value: 'Outfit, sans-serif', style: 'Contemporary and balanced' },
  { name: 'Sora', value: 'Sora, sans-serif', style: 'Bold and distinctive' },
];

const DEFAULT_BRANDING = {
  primaryColour: '#1a1a1a',
  accentColour: '#F6ADBC',
  fontFamily: 'Inter, sans-serif',
  fontName: 'Inter',
  logoUrl: null,
  businessName: '',
};

const Branding = () => {
  const { token, user } = useAuth();
  const [branding, setBranding] = useState(DEFAULT_BRANDING);
  const [savedBranding, setSavedBranding] = useState(DEFAULT_BRANDING);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [activeTab, setActiveTab] = useState('colours');
  const fileInputRef = useRef(null);

  useEffect(() => {
    fetchBranding();
    // Load Google Fonts for preview
    const link = document.createElement('link');
    link.href = 'https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;600;700&family=Inter:wght@400;600;700&family=Merriweather:wght@400;700&family=Outfit:wght@400;600;700&family=Playfair+Display:wght@400;700&family=Poppins:wght@400;600;700&family=Sora:wght@400;600;700&family=Space+Grotesk:wght@400;600;700&display=swap';
    link.rel = 'stylesheet';
    document.head.appendChild(link);
    return () => document.head.removeChild(link);
  }, []);

  const fetchBranding = async () => {
    try {
      const res = await axios.get(`${API}/branding`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.data.branding) {
        const b = { ...DEFAULT_BRANDING, ...res.data.branding };
        setBranding(b);
        setSavedBranding(b);
      }
    } catch (e) { /* first time — use defaults */ }
    setLoading(false);
  };

  const saveBranding = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/branding`, branding, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSavedBranding({ ...branding });
      toast.success('Branding saved — your Springboard is looking sharp');
    } catch (e) {
      toast.error('Could not save branding');
    }
    setSaving(false);
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Logo must be under 5MB');
      return;
    }
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await axios.post(`${API}/branding/logo`, formData, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' }
      });
      setBranding(prev => ({ ...prev, logoUrl: res.data.logo_url }));
      toast.success('Logo uploaded');
    } catch (e) {
      toast.error('Could not upload logo');
    }
    setUploading(false);
  };

  const resetBranding = () => {
    setBranding(DEFAULT_BRANDING);
    toast('Branding reset to defaults');
  };

  const hasChanges = JSON.stringify(branding) !== JSON.stringify(savedBranding);

  const tabs = [
    { id: 'colours', label: 'Colours', icon: Palette },
    { id: 'fonts', label: 'Typography', icon: Type },
    { id: 'logo', label: 'Logo', icon: Upload },
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="h-6 w-6 animate-spin text-black/30" />
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto" data-testid="branding-page">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center gap-3 mb-2">
          <div className="w-10 h-10 rounded-none bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center">
            <Sparkles className="h-5 w-5 text-white" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-black tracking-tight" data-testid="branding-heading">
              Customise Your Springboard
            </h1>
            <p className="text-sm text-black/50">Make your workspace feel like your business, not a template.</p>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
        {/* Left: Controls */}
        <div className="lg:col-span-2 space-y-4">
          {/* Tabs */}
          <div className="flex gap-1 bg-gray-100 p-1 rounded-none">
            {tabs.map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                data-testid={`branding-tab-${tab.id}`}
                className={`flex-1 flex items-center justify-center gap-2 py-2.5 px-3 rounded-none text-sm font-medium transition-all ${
                  activeTab === tab.id 
                    ? 'bg-white text-black shadow-sm' 
                    : 'text-black/45 hover:text-black/70'
                }`}
              >
                <tab.icon className="h-3.5 w-3.5" />
                {tab.label}
              </button>
            ))}
          </div>

          {/* Colours Tab */}
          {activeTab === 'colours' && (
            <div className="bg-white rounded-none border border-border p-5 space-y-5" data-testid="branding-colours-panel">
              <div>
                <label className="text-xs font-semibold uppercase tracking-wider text-black/40 block mb-3">Colour Palettes</label>
                <div className="grid grid-cols-4 gap-2">
                  {COLOUR_PALETTES.map((pal, i) => (
                    <button
                      key={i}
                      onClick={() => setBranding(prev => ({ ...prev, primaryColour: pal.primary, accentColour: pal.accent }))}
                      data-testid={`palette-${pal.name.toLowerCase()}`}
                      className={`relative p-2 rounded-none border-2 transition-all hover:scale-105 ${
                        branding.primaryColour === pal.primary && branding.accentColour === pal.accent
                          ? 'border-black '
                          : 'border-transparent hover:border-gray-200'
                      }`}
                    >
                      <div className="flex gap-1 mb-1.5">
                        <div className="w-5 h-5 rounded-none" style={{ background: pal.primary }} />
                        <div className="w-5 h-5 rounded-none" style={{ background: pal.accent }} />
                      </div>
                      <p className="text-[10px] font-medium text-black/60 text-center">{pal.name}</p>
                      {branding.primaryColour === pal.primary && branding.accentColour === pal.accent && (
                        <div className="absolute -top-1 -right-1 w-4 h-4 bg-black rounded-none flex items-center justify-center">
                          <Check className="h-2.5 w-2.5 text-white" />
                        </div>
                      )}
                    </button>
                  ))}
                </div>
              </div>

              <div className="border-t border-border pt-4">
                <label className="text-xs font-semibold uppercase tracking-wider text-black/40 block mb-3">Custom Colours</label>
                <div className="flex gap-4">
                  <div className="flex-1">
                    <label className="text-xs text-black/50 mb-1 block">Primary</label>
                    <div className="flex items-center gap-2">
                      <input
                        type="color"
                        value={branding.primaryColour}
                        onChange={(e) => setBranding(prev => ({ ...prev, primaryColour: e.target.value }))}
                        className="w-10 h-10 rounded-none cursor-pointer border-0"
                        data-testid="custom-primary-colour"
                      />
                      <input
                        type="text"
                        value={branding.primaryColour}
                        onChange={(e) => setBranding(prev => ({ ...prev, primaryColour: e.target.value }))}
                        className="flex-1 text-xs font-mono bg-gray-50 border border-gray-200 rounded-none px-3 py-2"
                      />
                    </div>
                  </div>
                  <div className="flex-1">
                    <label className="text-xs text-black/50 mb-1 block">Accent</label>
                    <div className="flex items-center gap-2">
                      <input
                        type="color"
                        value={branding.accentColour}
                        onChange={(e) => setBranding(prev => ({ ...prev, accentColour: e.target.value }))}
                        className="w-10 h-10 rounded-none cursor-pointer border-0"
                        data-testid="custom-accent-colour"
                      />
                      <input
                        type="text"
                        value={branding.accentColour}
                        onChange={(e) => setBranding(prev => ({ ...prev, accentColour: e.target.value }))}
                        className="flex-1 text-xs font-mono bg-gray-50 border border-gray-200 rounded-none px-3 py-2"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Fonts Tab */}
          {activeTab === 'fonts' && (
            <div className="bg-white rounded-none border border-border p-5" data-testid="branding-fonts-panel">
              <label className="text-xs font-semibold uppercase tracking-wider text-black/40 block mb-3">Choose a typeface</label>
              <div className="space-y-2">
                {FONT_OPTIONS.map((font, i) => (
                  <button
                    key={i}
                    onClick={() => setBranding(prev => ({ ...prev, fontFamily: font.value, fontName: font.name }))}
                    data-testid={`font-${font.name.toLowerCase().replace(/\s+/g, '-')}`}
                    className={`w-full text-left p-3.5 rounded-none border-2 transition-all ${
                      branding.fontName === font.name
                        ? 'border-black bg-gray-50'
                        : 'border-transparent hover:bg-gray-50 hover:border-gray-100'
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-base font-semibold" style={{ fontFamily: font.value }}>{font.name}</p>
                        <p className="text-xs text-black/40 mt-0.5">{font.style}</p>
                      </div>
                      {branding.fontName === font.name && (
                        <div className="w-5 h-5 bg-black rounded-none flex items-center justify-center">
                          <Check className="h-3 w-3 text-white" />
                        </div>
                      )}
                    </div>
                    <p className="text-sm text-black/60 mt-2" style={{ fontFamily: font.value }}>
                      The quick brown fox jumps over the lazy dog
                    </p>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Logo Tab */}
          {activeTab === 'logo' && (
            <div className="bg-white rounded-none border border-border p-5 space-y-5" data-testid="branding-logo-panel">
              <div>
                <label className="text-xs font-semibold uppercase tracking-wider text-black/40 block mb-1">Business Name</label>
                <input
                  type="text"
                  value={branding.businessName}
                  onChange={(e) => setBranding(prev => ({ ...prev, businessName: e.target.value }))}
                  placeholder="Your business name"
                  className="w-full bg-gray-50 border border-gray-200 rounded-none px-4 py-3 text-sm"
                  data-testid="branding-business-name"
                />
              </div>

              <div>
                <label className="text-xs font-semibold uppercase tracking-wider text-black/40 block mb-3">Logo</label>
                <input type="file" ref={fileInputRef} accept="image/*" onChange={handleLogoUpload} className="hidden" />
                
                {branding.logoUrl ? (
                  <div className="relative group">
                    <div className="w-full aspect-[3/1] bg-gray-50 rounded-none border-2 border-dashed border-gray-200 flex items-center justify-center overflow-hidden">
                      <img src={branding.logoUrl} alt="Business logo" className="max-h-full max-w-[80%] object-contain" data-testid="branding-logo-preview" />
                    </div>
                    <div className="mt-2 flex gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => fileInputRef.current?.click()}
                        className="rounded-none text-xs"
                      >
                        Replace
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setBranding(prev => ({ ...prev, logoUrl: null }))}
                        className="rounded-none text-xs text-red-600 hover:text-red-700"
                      >
                        Remove
                      </Button>
                    </div>
                  </div>
                ) : (
                  <button
                    onClick={() => fileInputRef.current?.click()}
                    disabled={uploading}
                    data-testid="branding-upload-logo-btn"
                    className="w-full aspect-[3/1] bg-gray-50 rounded-none border-2 border-dashed border-gray-300 hover:border-gray-400 flex flex-col items-center justify-center gap-2 transition-all cursor-pointer"
                  >
                    {uploading ? (
                      <Loader2 className="h-6 w-6 animate-spin text-black/30" />
                    ) : (
                      <>
                        <Upload className="h-6 w-6 text-black/30" />
                        <p className="text-sm text-black/40">Click to upload your logo</p>
                        <p className="text-xs text-black/25">PNG, SVG or JPG, max 5MB</p>
                      </>
                    )}
                  </button>
                )}
              </div>
            </div>
          )}

          {/* Actions */}
          <div className="flex items-center gap-3">
            <Button
              onClick={saveBranding}
              disabled={saving || !hasChanges}
              className="bg-[#1E491A] text-white hover:bg-[#333] rounded-none font-medium"
              data-testid="branding-save-btn"
            >
              {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />}
              Save Branding
            </Button>
            <Button
              variant="outline"
              onClick={resetBranding}
              className="rounded-none text-black/50"
              data-testid="branding-reset-btn"
            >
              <RotateCcw className="h-4 w-4 mr-2" />
              Reset
            </Button>
          </div>
        </div>

        {/* Right: Live Preview */}
        <div className="lg:col-span-3">
          <div className="sticky top-4">
            <div className="flex items-center gap-2 mb-3">
              <Eye className="h-4 w-4 text-black/40" />
              <p className="text-xs font-semibold uppercase tracking-wider text-black/40">Live Preview</p>
            </div>
            
            <div 
              className="bg-white rounded-none border border-border overflow-hidden "
              style={{ fontFamily: branding.fontFamily }}
              data-testid="branding-live-preview"
            >
              {/* Preview: Browser Chrome */}
              <div className="flex items-center gap-1.5 px-4 py-2.5 bg-gray-100 border-b border-gray-200">
                <div className="w-2.5 h-2.5 rounded-none bg-red-400" />
                <div className="w-2.5 h-2.5 rounded-none bg-yellow-400" />
                <div className="w-2.5 h-2.5 rounded-none bg-[#F6ADBC]" />
                <div className="flex-1 mx-4">
                  <div className="bg-white rounded-none px-3 py-1 text-[10px] text-black/30 text-center">
                    springboard.spring.co.nz
                  </div>
                </div>
              </div>

              {/* Preview: Sidebar + Content */}
              <div className="flex min-h-[460px]">
                {/* Mini Sidebar */}
                <div className="w-48 border-r border-gray-100 p-3 flex flex-col" style={{ background: branding.primaryColour }}>
                  {/* Logo area */}
                  <div className="mb-4 px-2 py-3 rounded-none" style={{ background: 'rgba(255,255,255,0.1)' }}>
                    {branding.logoUrl ? (
                      <img src={branding.logoUrl} alt="Logo" className="h-6 object-contain" />
                    ) : (
                      <p className="text-sm font-bold" style={{ color: branding.accentColour, fontFamily: branding.fontFamily }}>
                        {branding.businessName || 'Your Brand'}
                      </p>
                    )}
                  </div>
                  
                  {/* Nav items */}
                  {['Dashboard', 'Tasks', 'Documents', 'Calendar'].map((item, i) => (
                    <div
                      key={i}
                      className="px-3 py-2 rounded-none text-xs mb-0.5"
                      style={{
                        background: i === 0 ? 'rgba(255,255,255,0.15)' : 'transparent',
                        color: i === 0 ? branding.accentColour : 'rgba(255,255,255,0.5)',
                        fontFamily: branding.fontFamily,
                        fontWeight: i === 0 ? 600 : 400,
                      }}
                    >
                      {item}
                    </div>
                  ))}

                  <div className="flex-1" />
                  <div className="px-3 py-2 text-xs" style={{ color: 'rgba(255,255,255,0.3)', fontFamily: branding.fontFamily }}>
                    Powered by SPRING.
                  </div>
                </div>

                {/* Main Content */}
                <div className="flex-1 p-5">
                  <div className="flex items-center gap-3 mb-5">
                    <div className="w-8 h-8 rounded-none flex items-center justify-center text-xs font-bold" style={{ background: branding.accentColour, color: branding.primaryColour }}>
                      {(user?.name?.charAt(0) || 'U')}
                    </div>
                    <div>
                      <p className="text-sm font-semibold" style={{ color: branding.primaryColour, fontFamily: branding.fontFamily }}>
                        Welcome back, {user?.name?.split(' ')[0] || 'there'}
                      </p>
                      <p className="text-[11px] text-black/40" style={{ fontFamily: branding.fontFamily }}>
                        {branding.businessName || 'Your business'} — Stage 4: Business Setup
                      </p>
                    </div>
                  </div>

                  {/* Stats row */}
                  <div className="grid grid-cols-3 gap-2 mb-4">
                    {[
                      { label: 'Tasks Done', value: '12/18' },
                      { label: 'Current Stage', value: 'Stage 4' },
                      { label: 'Documents', value: '7' },
                    ].map((stat, i) => (
                      <div key={i} className="rounded-none p-3 border border-gray-100" style={{ fontFamily: branding.fontFamily }}>
                        <p className="text-[10px] text-black/40">{stat.label}</p>
                        <p className="text-base font-bold" style={{ color: branding.primaryColour }}>{stat.value}</p>
                      </div>
                    ))}
                  </div>

                  {/* Task cards */}
                  <div className="space-y-2">
                    <p className="text-xs font-semibold text-black/40 uppercase tracking-wider" style={{ fontFamily: branding.fontFamily }}>Up Next</p>
                    {[
                      { title: 'Register company name', done: true },
                      { title: 'Open business bank account', done: false },
                      { title: 'Upload brand assets', done: false },
                    ].map((task, i) => (
                      <div key={i} className="flex items-center gap-3 p-3 rounded-none border border-gray-100" style={{ fontFamily: branding.fontFamily }}>
                        <div
                          className="w-5 h-5 rounded-none flex items-center justify-center border-2"
                          style={{
                            background: task.done ? branding.accentColour : 'transparent',
                            borderColor: task.done ? branding.accentColour : '#ddd',
                          }}
                        >
                          {task.done && <Check className="h-3 w-3" style={{ color: branding.primaryColour }} />}
                        </div>
                        <p className={`text-xs ${task.done ? 'line-through text-black/30' : 'text-black/70'}`}>
                          {task.title}
                        </p>
                      </div>
                    ))}
                  </div>

                  {/* CTA */}
                  <button
                    className="mt-4 w-full py-2.5 rounded-none text-xs font-semibold transition-all"
                    style={{
                      background: branding.accentColour,
                      color: branding.primaryColour,
                      fontFamily: branding.fontFamily,
                    }}
                  >
                    Continue with Brian
                  </button>
                </div>
              </div>
            </div>

            <p className="text-xs text-black/30 text-center mt-3">
              This preview shows how your Springboard will look with your branding applied.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Branding;
